import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CheckmarkCircle01Icon,
  HourglassIcon,
  Copy01Icon,
  Alert01Icon,
  Mail01Icon,
  UserIcon,
  Money01Icon,
  BankIcon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveModal from '../components/ResponsiveModal';

const TransactionDetailPage = () => {
  const { transactionId } = useParams();
  const { user } = useAuth();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showEmailCopied, setShowEmailCopied] = useState(false);
  const [showPhoneCopied, setShowPhoneCopied] = useState(false);
  const [showAccountCopied, setShowAccountCopied] = useState(false);

  useEffect(() => {
    const fetchTransaction = async () => {
      setLoading(true);
      try {
        const endpoint = user?.role === 'agent'
          ? `/agent/transactions/${transactionId}`
          : `/client/transactions/${transactionId}`;

        const response = await api.get(endpoint);
        const tx = response.data?.data?.transaction || response.data?.transaction || response.data;
        
        // Normalize
        const normalized = {
          ...tx,
          id: tx.uuid || tx.uid,
          counterparty: user?.role === 'agent' ? tx.client : (tx.merchant || tx.order?.merchant),
          type: tx.type === 'payment_confirmation' ? (tx.order?.transaction_type || 'deposit') : tx.type,
          bank_account: tx.bank_account || tx.bankAccount || null
        };

        console.log("DETAIL DEBUG:", {
          type: normalized.type,
          order_type: normalized.order?.transaction_type,
          cashback_rate: normalized.order?.cashback_rate,
          spread: normalized.order?.spread
        });

        setTransaction(normalized);
      } catch (err) {
        console.error('Failed to fetch transaction:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchTransaction();
  }, [transactionId, user]);

  const handleCopyId = () => {
    const id = transaction.link_token || transaction.id;
    navigator.clipboard.writeText(id);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-soft-green"></div>
      </div>
    );
  }

  if (!transaction) return <div className="text-center py-20 text-muted-gray">Transaction not found.</div>;

  const timeline = [
    { status: 'Order Created', time: new Date(transaction.created_at).toLocaleTimeString(), isCompleted: true, isError: false },
    { 
      status: transaction.status === 'rejected' ? 'Transaction Rejected' : 'Funds Released', 
      time: new Date(transaction.updated_at).toLocaleTimeString(), 
      isCompleted: ['completed', 'verified'].includes(transaction.status),
      isError: transaction.status === 'rejected'
    },
  ];

  return (
    <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 pb-20">
      {/* Main Details */}
      <div className="lg:col-span-2 space-y-6">
        <div className="pb-4 border-b border-light-gray dark:border-dark-border">
          <p className="text-sm text-muted-gray">Transaction ID</p>
          <div className="flex items-center gap-2 overflow-hidden">
            <h1 className="text-2xl font-black font-mono text-charcoal dark:text-white tracking-tight break-all">{transaction.link_token || transaction.id}</h1>
            <button 
              onClick={handleCopyId}
              className={`transition-all duration-300 shrink-0 ${showCopied ? 'text-soft-green scale-110' : 'text-muted-gray hover:text-soft-green'}`}
            >
              <HugeiconsIcon icon={showCopied ? CheckmarkCircle01Icon : Copy01Icon} size={20} />
            </button>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float overflow-hidden">
          <div className="p-8">
            <h3 className="font-black text-lg text-charcoal dark:text-white mb-6">Timeline</h3>
            <ol className="relative border-l-2 border-gray-200 dark:border-gray-700 ml-2">                  
              {timeline.map((item, index) => (
                <li key={index} className="mb-8 ml-6 last:mb-0">            
                  <span className={`absolute flex items-center justify-center w-6 h-6 rounded-full -left-3 ring-4 ring-white dark:ring-dark-surface ${
                    item.isError ? 'bg-red-500' : (item.isCompleted ? 'bg-soft-green' : 'bg-gray-300 dark:bg-gray-600')
                  }`}>
                    {item.isError && <HugeiconsIcon icon={Alert01Icon} size={14} className="text-white"/>}
                    {item.isCompleted && !item.isError && <HugeiconsIcon icon={CheckmarkCircle01Icon} size={14} className="text-white"/>}
                  </span>
                  <h4 className={`font-bold ${item.isError ? 'text-red-500' : 'text-charcoal dark:text-white'}`}>{item.status}</h4>
                  <p className="text-xs text-muted-gray font-mono">{item.time}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float p-8 text-center">
          <p className="text-xs font-black text-muted-gray uppercase tracking-widest mb-2">{transaction.transaction_type} Amount</p>
          <p className="text-4xl font-black text-soft-green dark:text-light-mint tracking-tight">
            {transaction.currency === 'NGN' ? '₦' : '$'}{parseFloat(transaction.amount_sent || transaction.total_amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <div className={`inline-flex items-center gap-1.5 px-4 py-1.5 mt-4 rounded-full text-xs font-black uppercase tracking-wider ${
            ['completed', 'verified'].includes(transaction.status) 
              ? 'bg-green-100/50 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
              : transaction.status === 'rejected' 
                ? 'bg-red-100/50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-yellow-100/50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          }`}>
            <HugeiconsIcon icon={
              ['completed', 'verified'].includes(transaction.status) ? CheckmarkCircle01Icon : 
              transaction.status === 'rejected' ? Alert01Icon : HourglassIcon
            } size={14} />
            {transaction.status}
          </div>
          
          <div className="mt-8 pt-6 border-t border-light-gray/20 dark:border-dark-border/20 w-full text-left space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-muted-gray">Date</span>
              <span className="text-sm font-bold text-charcoal dark:text-white">{new Date(transaction.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-gray">{user?.role === 'agent' ? 'Client' : 'Agent'}</span>
              <span className="text-sm font-bold text-charcoal dark:text-white">{transaction.counterparty?.name || 'Private User'}</span>
            </div>
            {transaction.bank_account && (
              <div className="pt-4 border-t border-light-gray/20 dark:border-dark-border/20">
                <span className="text-sm text-muted-gray">Bank Details</span>
                <div className="mt-2 bg-off-white dark:bg-gray-800 rounded-xl p-3 border border-light-gray/20 dark:border-gray-700/20">
                  <div className="flex items-center gap-2 mb-2">
                    <BankIcon size={16} className="text-soft-green" />
                    <span className="text-xs font-bold text-charcoal dark:text-white">{transaction.bank_account.bank_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-muted-gray">{transaction.bank_account.account_number}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(transaction.bank_account.account_number);
                        setShowAccountCopied(true);
                        setTimeout(() => setShowAccountCopied(false), 2000);
                      }}
                      className={`transition-all duration-300 ${showAccountCopied ? 'text-soft-green scale-110' : 'text-muted-gray hover:text-soft-green'}`}
                    >
                      <HugeiconsIcon icon={showAccountCopied ? CheckmarkCircle01Icon : Copy01Icon} size={14}/>
                    </button>
                  </div>
                  <p className="text-xs text-muted-gray mt-1 truncate">{transaction.bank_account.account_name}</p>
                </div>
              </div>
            )}
          </div>

          {transaction.type === 'deposit' && transaction.order?.cashback_rate > 0 && transaction.status === 'completed' && (
            <div className="mt-6 p-6 bg-purple-500/10 border border-purple-500/20 rounded-[2rem] text-left animate-in zoom-in-95 duration-300">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-purple-500/20 rounded-lg text-purple-500">
                  <HugeiconsIcon icon={Money01Icon} size={14} />
                </div>
                <p className="text-[10px] font-black text-purple-500 uppercase tracking-widest">Cashback Reward</p>
              </div>
              <p className="text-2xl font-black text-purple-600">₦{((parseFloat(transaction.amount_sent) / parseFloat(transaction.order.current_rate)) * parseFloat(transaction.order.spread) * parseFloat(transaction.order.cashback_rate)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-[9px] font-bold text-muted-gray mt-1 leading-tight">This profit-share was automatically added to the client&apos;s rewards wallet.</p>
            </div>
          )}
        </div>

        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float p-6">
          <h3 className="font-black text-lg text-charcoal dark:text-white mb-4">Need Help?</h3>
          <p className="text-sm text-muted-gray mb-6">If you have an issue with this transaction, you can contact the {user?.role === 'agent' ? 'client' : 'agent'} or flag for admin review.</p>
          <div className="flex flex-col gap-3">
             <Button onClick={() => setIsContactModalOpen(true)} variant="secondary" className="w-full gap-2 py-4 rounded-2xl font-black">
                <HugeiconsIcon icon={Mail01Icon} size={18} />
                Contact {user?.role === 'agent' ? 'Client' : 'Agent'}
             </Button>
             <Button 
                onClick={() => window.location.href = `mailto:support@safzi.com?subject=Transaction Review: ${transaction.reference}&body=Hi Safzi Support,%0D%0A%0D%0AI would like to request a review for transaction ${transaction.reference}.%0D%0A%0D%0AIssue details:%0D%0A[Enter details here]`} 
                variant="danger" 
                className="w-full gap-2 py-4 rounded-2xl font-black text-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 border-none"
             >
                <HugeiconsIcon icon={Alert01Icon} size={18} />
                Flag for Review
             </Button>
          </div>
        </div>
      </div>

      <ResponsiveModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} title="Contact Details">
        <div className="py-6 space-y-6">
          <div className="text-center">
            <div className="bg-soft-green/10 p-4 rounded-full inline-block mb-4 text-soft-green">
              <HugeiconsIcon icon={UserIcon} size={32} />
            </div>
            <h3 className="text-xl font-black text-charcoal dark:text-white">{transaction.counterparty?.name || 'User'}</h3>
            <p className="text-sm text-muted-gray">{user?.role === 'agent' ? 'Client' : 'Authorized Agent'}</p>
          </div>

          <div className="bg-off-white dark:bg-gray-800 p-6 rounded-3xl space-y-4">
            {user?.role !== 'agent' && (
              <div>
                <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Email Address</p>
                <div className="flex justify-between items-center">
                  <p className="font-bold text-charcoal dark:text-white select-all">{transaction.counterparty?.email || 'N/A'}</p>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(transaction.counterparty?.email);
                      setShowEmailCopied(true);
                      setTimeout(() => setShowEmailCopied(false), 2000);
                    }} 
                    className={`transition-all duration-300 ${showEmailCopied ? 'text-soft-green scale-110' : 'text-muted-gray hover:text-soft-green'}`}
                  >
                    <HugeiconsIcon icon={showEmailCopied ? CheckmarkCircle01Icon : Copy01Icon} size={16}/>
                  </button>
                </div>
              </div>
            )}
            {transaction.counterparty?.phone && (
              <div className={user?.role !== 'agent' ? "pt-4 border-t border-gray-200 dark:border-gray-700" : ""}>
                <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Phone Number</p>
                <div className="flex justify-between items-center">
                  <p className="font-bold text-charcoal dark:text-white select-all">{transaction.counterparty?.phone}</p>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(transaction.counterparty?.phone);
                      setShowPhoneCopied(true);
                      setTimeout(() => setShowPhoneCopied(false), 2000);
                    }} 
                    className={`transition-all duration-300 ${showPhoneCopied ? 'text-soft-green scale-110' : 'text-muted-gray hover:text-soft-green'}`}
                  >
                    <HugeiconsIcon icon={showPhoneCopied ? CheckmarkCircle01Icon : Copy01Icon} size={16}/>
                  </button>
                </div>
              </div>
            )}
          </div>

          <Button onClick={() => setIsContactModalOpen(false)} className="w-full py-4 rounded-2xl font-black">Close</Button>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default TransactionDetailPage;
